/**
 * kleinFilmPipeline — Klein bottle viewed from above with iridescent
 * thin-film interference coating, time-varying thickness.
 *
 * Composition:
 *   1. Background — iridescent gradient backdrop with FBM texture.
 *   2. Klein bottle silhouette — figure-8 immersion rendered as a
 *      2D projection with surface shading.
 *   3. Thin-film interference — rainbow color mapped via film
 *      thickness, modulated by audio + radial gradient.
 *   4. Reflective highlights — specular reflections on the surface.
 *   5. Refraction sample — distorted background visible through
 *      the bottle.
 *   6. Internal shimmer — fine caustic-like shimmer inside.
 *
 * Audio reactivity:
 *   bass — shifts the interference palette, brightens core
 *   mid  — varies film thickness in time
 *   high — adds fine rainbow sparkle across surface
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface KleinFilmPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float h11(float n) {
  return fract(sin(n * 91.345) * 47453.12);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float r = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    r += a * noise2(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return r;
}

// Klein bottle figure-8 immersion — 3D point from (u, v) parameters.
vec3 kleinPoint(float u, float v) {
  float a = 2.0;
  float cu2 = cos(u * 0.5);
  float su2 = sin(u * 0.5);
  float cu = cos(u);
  float su = sin(u);
  float sv = sin(v);
  float s2v = sin(2.0 * v);
  float r = a + cu2 * sv - su2 * s2v;
  return vec3(r * cu * 0.5, r * su * 0.5, su2 * sv + cu2 * s2v) * 0.5;
}

// Project the Klein bottle to 2D using a fixed isometric-ish view.
vec2 kleinProj(float u, float v) {
  vec3 p3 = kleinPoint(u, v);
  // Slight perspective.
  return p3.xy * 0.45 + vec2(p3.z * 0.05, p3.z * 0.05);
}

// Distance to Klein bottle via 2D parameter sweep.
float kleinDist2D(vec2 p) {
  float minD = 1000.0;
  for (int i = 0; i < 32; i++) {
    float u = float(i) / 32.0 * 6.2831;
    for (int j = 0; j < 16; j++) {
      float v = float(j) / 16.0 * 6.2831;
      vec2 q = kleinProj(u, v);
      float d = length(p - q);
      if (d < minD) minD = d;
    }
  }
  return minD;
}

// Find which (u, v) is closest, returning a smooth surface id.
vec3 kleinClosestUV(vec2 p) {
  float minD = 1000.0;
  float bestU = 0.0;
  float bestV = 0.0;
  for (int i = 0; i < 32; i++) {
    float u = float(i) / 32.0 * 6.2831;
    for (int j = 0; j < 16; j++) {
      float v = float(j) / 16.0 * 6.2831;
      vec2 q = kleinProj(u, v);
      float d = length(p - q);
      if (d < minD) {
        minD = d;
        bestU = u;
        bestV = v;
      }
    }
  }
  return vec3(bestU, bestV, minD);
}

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // --- 1. Background iridescent gradient ---
  float bgT = clamp((p.y + 0.7) / 1.4, 0.0, 1.0);
  // Multi-stop rainbow gradient.
  vec3 c1 = vec3(0.05, 0.02, 0.12);
  vec3 c2 = vec3(0.10, 0.03, 0.20);
  vec3 c3 = vec3(0.04, 0.06, 0.18);
  vec3 bg = mix(c1, c2, smoothstep(0.0, 0.5, bgT));
  bg = mix(bg, c3, smoothstep(0.5, 1.0, bgT));
  // FBM texture.
  bg += vec3(0.20, 0.10, 0.40) * fbm(p * 2.5 + vec2(uTime * 0.05, uTime * 0.03)) * 0.3;

  vec3 col = bg;

  // --- 2. Klein bottle 2D shape ---
  vec3 surf = kleinClosestUV(p);
  float minD = surf.z;
  float bestU = surf.x;
  float bestV = surf.y;

  // Surface shell with anti-aliased edge.
  float shellT = smoothstep(0.030, 0.020, minD);
  float innerShell = smoothstep(0.005, 0.0, minD);

  // Surface parameter for color & thickness.
  // Use u (around) and v (along) for thin-film variation.
  float surfaceId = bestU / 6.2831;
  float surfaceV = bestV / 6.2831;

  // --- 3. Thin-film interference ---
  // Film thickness varies over surface and time.
  float thickness = 600.0
    + 200.0 * sin(surfaceId * 8.0 + uTime * (0.5 + mid * 0.8))
    + 100.0 * cos(surfaceV * 12.0 - uTime * (0.7 + mid * 1.2))
    + 80.0 * sin((surfaceId + surfaceV) * 6.0);

  // Refractive index shift → wavelength interference.
  // Compute R, G, B intensities from phase differences.
  float phaseR = thickness * 0.012 + uTime * 0.3 + surfaceId * 2.0;
  float phaseG = thickness * 0.014 + uTime * 0.3 + surfaceId * 2.0 + 1.5;
  float phaseB = thickness * 0.016 + uTime * 0.3 + surfaceId * 2.0 + 3.0;
  vec3 filmCol = 0.5 + 0.5 * vec3(
    cos(phaseR),
    cos(phaseG),
    cos(phaseB)
  );
  // Slightly boost saturation.
  filmCol = pow(filmCol, vec3(0.85));
  filmCol *= 1.2;

  // Audio shifts palette.
  filmCol = mix(filmCol, filmCol.gbr, bass * 0.6);

  // --- 4. Reflective specular highlight ---
  // Compute approximate normal from surface gradient via local sampling.
  vec2 lightDir = normalize(vec2(0.5, 0.7));
  float specAngle = mod(bestU + uTime * 0.4, 6.2831);
  float specMask = pow(max(0.0, cos(specAngle - 3.14159)), 30.0);
  specMask += pow(max(0.0, cos(specAngle - 4.7124)), 30.0);

  // Surface color = film + spec.
  vec3 surfaceCol = filmCol * (0.5 + innerShell * 0.6);
  surfaceCol += vec3(1.0, 1.0, 1.0) * specMask * 0.6;

  // --- 5. Apply surface to background ---
  // Mix with strong shell.
  col = mix(col, surfaceCol, shellT);

  // --- 6. Refraction sample (subtle distortion) ---
  if (innerShell > 0.0) {
    vec2 refOff = vec2(
      sin(p.x * 6.0 + uTime),
      cos(p.y * 6.0 + uTime)
    ) * 0.02;
    vec3 refracted = bg + 0.2 * vec3(
      fbm(p * 4.0 + refOff),
      fbm(p * 4.0 + refOff + 5.0),
      fbm(p * 4.0 + refOff + 10.0)
    );
    col = mix(col, refracted * 0.6 + surfaceCol * 0.4, innerShell * 0.7);
  }

  // --- 7. Internal shimmer ---
  float shimmer = noise2(p * 80.0 + uTime * 4.0);
  shimmer = smoothstep(0.7, 0.95, shimmer) * innerShell;
  col += vec3(1.0, 0.95, 1.0) * shimmer * (0.5 + high * 0.8);

  // --- 8. Rainbow sparkle on surface ---
  float sparkle = noise2(p * 200.0 + uTime * 6.0);
  sparkle = smoothstep(0.85, 0.98, sparkle) * shellT;
  col += vec3(1.0) * sparkle * (0.4 + high * 0.8);

  // Outer glow halo.
  float halo = exp(-max(0.0, minD - 0.005) * 15.0) * smoothstep(0.005, 0.05, minD);
  col += vec3(0.5, 0.4, 0.9) * halo * (0.3 + bass * 0.5);

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.3;

  // Tone mapping + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeKleinFilmBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 1.0);
  out[1] = 0.5 + 0.4 * Math.abs(Math.sin(t * 1.8));
  out[2] = 0.4 + 0.4 * Math.sin(t * 2.6 + 0.7);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.5 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 2.2 + 2.3);
  out[5] = 0.4 + 0.5 * Math.sin(t * 3.0 + 3.1);
  out[6] = 0.3 + 0.6 * Math.sin(t * 5.0 + 0.5);
  out[7] = 0.4 + 0.5 * Math.sin(t * 7.0 + 1.7);
  out[8] = 0.3 + 0.6 * Math.sin(t * 9.0 + 2.1);
}

export function computeKleinFilmRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.012], [0.012, 0.040], [0.040, 0.080],
    [0.080, 0.180], [0.180, 0.300], [0.300, 0.460],
    [0.460, 0.640], [0.640, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0;
    let count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createKleinFilmPipeline(gl: WebGL2RenderingContext): KleinFilmPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    bands: new Float32Array(9),
  };
}

export function drawKleinFilm(
  gl: WebGL2RenderingContext,
  pipe: KleinFilmPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}